import { useRef, useState } from 'react'
import { useProjectFiles, useUploadProjectFile } from '../api/queries'
import { prepareDocumentFile } from '../lib/documentPhoto'
import { useAuthedBlob } from '../lib/photos'

/**
 * Vælg et billede til et punkt på mangellisten (beslutning 058).
 *
 * <p>
 * Billedet er altid en fil på <b>sagen</b> - der laves ikke et parallelt sted at
 * gemme fotos. Enten vælger man et, der allerede ligger der, eller man tager et
 * nyt, som lægges i sagens mappe som alle andre (beslutning 023).
 * </p>
 *
 * "Tag billede" åbner bagkameraet direkte på mobilen, fordi det er dér, det
 * bliver brugt: manden står ved manglen, når han skriver den op.
 */
export function PhotoPicker({
  projectId,
  value,
  onChange,
  label
}: {
  projectId: number
  value?: number | null
  onChange: (fileId: number | null) => void
  label: string
}) {
  const { data: files } = useProjectFiles(projectId)
  // Samme vej op som sagens oevrige billeder: skaleret i browseren og gemt i
  // sagens mappe i SharePoint (beslutning 023).
  const upload = useUploadProjectFile(projectId)
  const cameraInput = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const images = (files ?? []).filter((f) => f.isImage)
  const selected = images.find((f) => f.id === value)

  async function takePhoto(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setError(null)
    setBusy(true)
    try {
      const prepared = await prepareDocumentFile(file)
      const created = await upload.mutateAsync({
        file: prepared.blob,
        fileName: prepared.fileName,
        // Manglen hoerer til "andet" - den er hverken et foer- eller efterbillede
        // af selve arbejdet.
        category: 'other'
      })

      onChange(created.id)
      setOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Billedet kunne ikke lægges op.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="field">
      <span className="field__label">{label}</span>

      {selected ? (
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <Thumbnail projectId={projectId} fileId={selected.id} />
          <div style={{ flex: 1 }}>
            <div className="small">{selected.name}</div>
            {selected.caption && <div className="tiny muted">{selected.caption}</div>}
          </div>
          <button className="btn btn--sm" type="button" onClick={() => onChange(null)}>
            Fjern
          </button>
        </div>
      ) : (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <input
            ref={cameraInput}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => void takePhoto(e)}
          />
          <button
            className="btn btn--sm"
            type="button"
            disabled={busy}
            onClick={() => cameraInput.current?.click()}
          >
            {busy ? 'Gemmer ...' : 'Tag billede'}
          </button>

          {images.length > 0 && (
            <button className="btn btn--sm" type="button" onClick={() => setOpen((o) => !o)}>
              {open ? 'Luk' : `Vælg blandt sagens ${images.length}`}
            </button>
          )}

          {images.length === 0 && (
            <span className="tiny muted" style={{ alignSelf: 'center' }}>
              Billedet lægges i sagens mappe
            </span>
          )}
        </div>
      )}

      {error && <div className="error-box" style={{ marginTop: 6 }}>{error}</div>}

      {open && !selected && (
        <div
          className="row"
          style={{ gap: 6, flexWrap: 'wrap', marginTop: 8, maxHeight: 200, overflowY: 'auto' }}
        >
          {images.map((f) => (
            <button
              key={f.id}
              type="button"
              className="btn"
              style={{ padding: 2, border: '1px solid var(--border)' }}
              title={f.caption ?? f.name}
              onClick={() => {
                onChange(f.id)
                setOpen(false)
              }}
            >
              <Thumbnail projectId={projectId} fileId={f.id} />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Miniaturen hentes bag authentication - billederne ligger i SharePoint og kan
 * ikke hentes med et almindeligt img-tag.
 */
function Thumbnail({ projectId, fileId }: { projectId: number; fileId: number }) {
  const url = useAuthedBlob(`/api/projects/${projectId}/files/${fileId}/thumbnail`)

  if (!url) {
    return (
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 6,
          background: 'var(--surface-2)',
          flexShrink: 0
        }}
      />
    )
  }

  return (
    <img
      src={url}
      alt=""
      style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, display: 'block' }}
    />
  )
}
